import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { ArrowUpRight, CalendarRange, KanbanSquare } from "lucide-react";
import { z } from "zod";

import {
  useBoardFeatureDependencies,
  useBoardFeatures,
  useMilestones,
  useMvpComponents,
  useUpdateBoardFeature,
  useWorkstreams,
} from "@/application/use-cases/board/use-board.queries";
import { MilestoneRail } from "@/presentation/components/planner/milestone-rail";
import { PlannerDrawerContent } from "@/presentation/components/planner/planner-drawer-content";
import { PlannerSkeleton } from "@/presentation/components/planner/planner-skeleton";
import { PlannerTaskCard } from "@/presentation/components/planner/planner-task-card";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import { ViewOnlyBadge } from "@/presentation/components/shell/view-only-badge";
import { useWorkspace } from "@/presentation/providers/workspace.provider";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@monorepo-apps/ui/src/components/sheet";
import { cn } from "@monorepo-apps/ui/src/lib/utils";
import {
  buildPlannerGraph,
  compareForOrder,
  fmtShort,
  isActive,
  isDone,
  isOverdue,
  isReady,
  makeAtRisk,
  milestoneHealth,
  projectStartMs,
  weekOf,
  weekStartDate,
} from "@/presentation/data/planner";
import type { BoardFeatureRow, WorkstreamRow } from "@/infrastructure/supabase/database.types";

const searchSchema = z.object({
  mode: z.enum(["today", "week"]).optional(),
  week: z.number().optional(),
  ms: z.string().optional(),
  item: z.string().optional(),
});

export const Route = createFileRoute("/_app/planner")({
  validateSearch: searchSchema,
  component: PlannerPage,
});

function PlannerPage() {
  const featuresQ = useBoardFeatures();
  const workstreamsQ = useWorkstreams();
  const milestonesQ = useMilestones();
  const componentsQ = useMvpComponents();
  const depsQ = useBoardFeatureDependencies();
  const update = useUpdateBoardFeature();
  const { canWrite } = useWorkspace();
  const readOnly = !canWrite;

  const navigate = useNavigate({ from: Route.fullPath });
  const { mode = "today", week, ms: msFilter, item: openId } = Route.useSearch();

  const features = featuresQ.data ?? [];
  const workstreams = workstreamsQ.data ?? [];
  const milestones = milestonesQ.data ?? [];
  const components = componentsQ.data ?? [];
  const deps = depsQ.data ?? [];

  const todayMs = useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  }, []);
  const startMs = useMemo(() => projectStartMs(milestones), [milestones]);
  const currentWeek = Math.max(
    1,
    weekOf(new Date(todayMs).toISOString().slice(0, 10), startMs) ?? 1
  );
  const weekSel = week ?? currentWeek;

  const graph = useMemo(() => buildPlannerGraph(features, deps), [features, deps]);
  const atRisk = useMemo(() => makeAtRisk(graph, todayMs), [graph, todayMs]);
  const order = useMemo(() => compareForOrder(graph), [graph]);
  const componentsById = useMemo(() => new Map(components.map((c) => [c.id, c])), [components]);

  const health = useMemo(
    () =>
      milestones
        .map((m) => milestoneHealth(m, features, todayMs, atRisk))
        .filter((h) => h.total > 0),
    [milestones, features, graph, todayMs, atRisk]
  );

  const maxWeek = useMemo(() => {
    let mx = currentWeek;
    for (const f of features) {
      const w = weekOf(f.due_date, startMs);
      if (w && w > mx) mx = w;
    }
    return mx;
  }, [features, startMs, currentWeek]);

  const inScope = (f: BoardFeatureRow) => !msFilter || f.milestone_key === msFilter;
  const setSearch = (patch: Record<string, unknown>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }) });

  const stats = useMemo(() => {
    const sc = features.filter(inScope);
    return {
      ready: sc.filter((f) => isReady(f, graph)).length,
      active: sc.filter(isActive).length,
      overdue: sc.filter((f) => isOverdue(f, todayMs)).length,
      done: sc.filter(isDone).length,
      total: features.length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features, graph, todayMs, msFilter]);

  const openFeature = openId ? (graph.byId.get(openId) ?? null) : null;

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-[100rem] min-w-0 flex-col gap-6 p-6 lg:gap-8 lg:p-10">
      <RouteHero
        eyebrow="Build Planner"
        icon={CalendarRange}
        numeral={String(stats.total).padStart(3, "0")}
        numeralTone="cyan"
        numeralSuffix="features"
        title="What to build, in what order, and are we on track"
        summary="Each day: what's ready to start. Each week: the order (by leverage → critical → title). Each task: its prerequisites and what it unblocks. Milestone health flags what's at risk — today is the live date."
        collapsibleSummary
        meta={[
          { label: "Ready now", value: stats.ready, tone: "cyan" },
          { label: "In progress", value: stats.active, tone: "amber" },
          { label: "Overdue", value: stats.overdue, tone: "red" },
          { label: "Done", value: stats.done, tone: "green" },
        ]}
        actions={
          <>
            {readOnly ? <ViewOnlyBadge /> : null}
            <Link
              to="/board"
              className="group inline-flex items-center justify-between gap-2 rounded-lg border border-[oklch(1_0_0_/_0.12)] bg-[oklch(1_0_0_/_0.04)] px-4 py-2.5 text-[13px] font-medium text-[oklch(0.93_0.008_240)] transition-all hover:border-[oklch(0.72_0.17_190_/_0.5)] hover:bg-[oklch(0.72_0.17_190_/_0.08)] print:hidden"
            >
              <span className="inline-flex items-center gap-2">
                <KanbanSquare className="size-3.5" />
                <span className="font-data text-[11px] tracking-[0.12em] uppercase">
                  Open Delivery Board
                </span>
              </span>
              <ArrowUpRight className="size-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </>
        }
      />

      {featuresQ.isLoading ? (
        <PlannerSkeleton />
      ) : (
        <>
          {/* Milestone health rail */}
          <section className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between">
              <span className="eyebrow">
                Milestone health · today {fmtShort(new Date(todayMs))}
              </span>
              {msFilter ? (
                <button
                  type="button"
                  onClick={() => setSearch({ ms: undefined })}
                  className="font-data text-primary text-[11.5px]"
                >
                  ✕ clear filter
                </button>
              ) : (
                <span className="text-muted-foreground text-[11px]">
                  click a milestone to focus its tasks
                </span>
              )}
            </div>
            <MilestoneRail
              health={health}
              selectedKey={msFilter ?? null}
              onSelect={(key) => setSearch({ ms: key ?? undefined })}
            />
          </section>

          {/* mode + week selector */}
          <div className="flex flex-wrap items-end gap-4 print:hidden">
            <div className="flex flex-col gap-1.5">
              <span className="eyebrow">Mode</span>
              <div className="border-border bg-card inline-flex gap-0.5 rounded-lg border p-0.5 shadow-[0_0_0_1px_oklch(0.55_0.17_190_/_0.06)]">
                {(["today", "week"] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setSearch({ mode: m })}
                    className={cn(
                      "rounded-md px-3.5 py-1.5 text-[12.5px] font-medium transition-colors",
                      mode === m
                        ? "text-primary bg-[oklch(0.55_0.17_190_/_0.12)]"
                        : "text-muted-foreground hover:bg-muted"
                    )}
                  >
                    {m === "today" ? "Today" : "By week"}
                  </button>
                ))}
              </div>
            </div>
            {mode === "week" ? (
              <div className="flex flex-col gap-1.5">
                <span className="eyebrow">Week</span>
                <div className="flex flex-wrap gap-1">
                  {Array.from({ length: maxWeek }, (_, i) => i + 1).map((w) => (
                    <button
                      key={w}
                      type="button"
                      onClick={() => setSearch({ week: w })}
                      className={cn(
                        "font-data border-border rounded-md border px-2 py-1 text-[10px]",
                        w === weekSel
                          ? "text-primary border-[oklch(0.55_0.17_190_/_0.35)] bg-[oklch(0.55_0.17_190_/_0.12)]"
                          : "bg-card text-muted-foreground",
                        w === currentWeek && w !== weekSel
                          ? "shadow-[inset_0_-2px_0_var(--rag-red)]"
                          : ""
                      )}
                    >
                      W{w} · {fmtShort(weekStartDate(w, startMs))}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          {mode === "today" ? (
            <TodayView
              features={features.filter(inScope)}
              workstreams={workstreams}
              graph={graph}
              order={order}
              todayMs={todayMs}
              msFilter={msFilter ?? null}
              onOpen={(id) => setSearch({ item: id })}
            />
          ) : (
            <WeekView
              features={features.filter(inScope)}
              workstreams={workstreams}
              graph={graph}
              order={order}
              todayMs={todayMs}
              startMs={startMs}
              weekSel={weekSel}
              onOpen={(id) => setSearch({ item: id })}
            />
          )}
        </>
      )}

      <Sheet open={!!openId} onOpenChange={(o) => !o && setSearch({ item: undefined })}>
        <SheetContent side="right" className="w-full p-0 sm:max-w-xl">
          <SheetTitle className="sr-only">Task detail</SheetTitle>
          <SheetDescription className="sr-only">
            Prerequisites, what this unblocks, status and due date.
          </SheetDescription>
          {openFeature ? (
            <PlannerDrawerContent
              feature={openFeature}
              workstreamLabel={wsLabel(workstreams, openFeature.workstream_key)}
              componentLabel={
                openFeature.component_id
                  ? (componentsById.get(openFeature.component_id)?.name_en ?? null)
                  : null
              }
              upstream={(graph.prereqs.get(openFeature.id) ?? [])
                .map((p) => graph.byId.get(p))
                .filter((f): f is BoardFeatureRow => Boolean(f))
                .sort(order)}
              downstream={(graph.dependents.get(openFeature.id) ?? [])
                .map((d) => graph.byId.get(d))
                .filter((f): f is BoardFeatureRow => Boolean(f))
                .sort(order)}
              leverage={graph.leverage.get(openFeature.id) ?? 0}
              onOpen={(id) => setSearch({ item: id })}
              onPatch={(patch) => {
                if (readOnly) return;
                update.mutate({ id: openFeature.id, patch });
              }}
              readOnly={readOnly}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function wsLabel(ws: WorkstreamRow[], key: string): string {
  const w = ws.find((x) => x.key === key);
  return w ? `Block ${w.code} · ${w.title}` : key;
}

interface ColInputs {
  features: BoardFeatureRow[];
  workstreams: WorkstreamRow[];
  graph: ReturnType<typeof buildPlannerGraph>;
  order: (a: BoardFeatureRow, b: BoardFeatureRow) => number;
  todayMs: number;
  onOpen: (id: string) => void;
}

function TodayView({
  features,
  workstreams,
  graph,
  order,
  todayMs,
  msFilter,
  onOpen,
}: ColInputs & { msFilter: string | null }) {
  const inprog = features.filter(isActive);
  const ready = features.filter((f) => isReady(f, graph));
  const cols = workstreams
    .map((w) => ({
      w,
      ip: inprog.filter((f) => f.workstream_key === w.key).sort(order),
      rd: ready.filter((f) => f.workstream_key === w.key).sort(order),
    }))
    .filter((c) => c.ip.length || c.rd.length);

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-[15px] font-semibold tracking-tight">
        What to implement now{msFilter ? ` · ${msFilter.toUpperCase()}` : ""}
      </h3>
      <p className="text-muted-foreground -mt-1 mb-1 text-[12.5px]">
        Tasks whose prerequisites are all done — ready to start — plus what's in progress, by
        workstream, ordered by leverage.
      </p>
      {cols.length === 0 ? (
        <p className="text-muted-foreground text-[13px] italic">
          Nothing ready or in progress in scope — everything is blocked or done.
        </p>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
          {cols.map(({ w, ip, rd }) => (
            <ColumnShell
              key={w.key}
              w={w}
              graph={graph}
              meta={`${ip.length + rd.length} actionable`}
            >
              {ip.length ? <SubHead>Continue (in progress)</SubHead> : null}
              {ip.map((f) => (
                <PlannerTaskCard
                  key={f.id}
                  feature={f}
                  leverage={graph.leverage.get(f.id) ?? 0}
                  rank={null}
                  todayMs={todayMs}
                  onOpen={() => onOpen(f.id)}
                />
              ))}
              {rd.length ? <SubHead>Start now (ready)</SubHead> : null}
              {rd.map((f) => (
                <PlannerTaskCard
                  key={f.id}
                  feature={f}
                  leverage={graph.leverage.get(f.id) ?? 0}
                  rank={null}
                  todayMs={todayMs}
                  onOpen={() => onOpen(f.id)}
                />
              ))}
            </ColumnShell>
          ))}
        </div>
      )}
    </div>
  );
}

function WeekView({
  features,
  workstreams,
  graph,
  order,
  todayMs,
  startMs,
  weekSel,
  onOpen,
}: ColInputs & { startMs: number; weekSel: number }) {
  const cols = workstreams
    .map((w) => ({
      w,
      fs: features
        .filter((f) => f.workstream_key === w.key && weekOf(f.due_date, startMs) === weekSel)
        .sort(order),
    }))
    .filter((c) => c.fs.length);

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-[15px] font-semibold tracking-tight">
        Week {weekSel} — recommended order
      </h3>
      <p className="text-muted-foreground -mt-1 mb-1 text-[12.5px]">
        Tasks due this week per workstream, numbered in implementation order: leverage →
        critical/MVP-blocking → title.
      </p>
      {cols.length === 0 ? (
        <p className="text-muted-foreground text-[13px] italic">No tasks due this week in scope.</p>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
          {cols.map(({ w, fs }) => (
            <ColumnShell key={w.key} w={w} graph={graph} meta={`${fs.length} to start`}>
              {fs.map((f, i) => (
                <PlannerTaskCard
                  key={f.id}
                  feature={f}
                  leverage={graph.leverage.get(f.id) ?? 0}
                  rank={i + 1}
                  todayMs={todayMs}
                  onOpen={() => onOpen(f.id)}
                />
              ))}
            </ColumnShell>
          ))}
        </div>
      )}
    </div>
  );
}

function ColumnShell({
  w,
  graph,
  meta,
  children,
}: {
  w: WorkstreamRow;
  graph: ReturnType<typeof buildPlannerGraph>;
  meta: string;
  children: React.ReactNode;
}) {
  const all = [...graph.byId.values()].filter((f) => f.workstream_key === w.key);
  const done = all.filter(isDone).length;
  const pct = all.length ? Math.round((done / all.length) * 100) : 0;
  return (
    <section className="glow-card-static overflow-hidden rounded-xl">
      <header className="border-border flex flex-col gap-1.5 border-b px-3.5 py-2.5">
        <div className="flex items-center gap-2">
          <span className="font-data text-[13px] font-semibold">{w.code}</span>
          <span className="text-muted-foreground truncate text-[11.5px]">{w.title}</span>
        </div>
        <div className="bg-muted flex h-[5px] overflow-hidden rounded">
          <span style={{ width: `${pct}%`, background: "var(--rag-green)" }} />
        </div>
        <div className="font-data text-muted-foreground flex justify-between text-[9.5px] tracking-[0.06em] uppercase">
          <span>{meta}</span>
          <span>
            {done}/{all.length} done
          </span>
        </div>
      </header>
      <div>{children}</div>
    </section>
  );
}

function SubHead({ children }: { children: React.ReactNode }) {
  return (
    <div className="eyebrow border-border/60 border-t px-3.5 pt-2 pb-0.5 first:border-t-0">
      {children}
    </div>
  );
}
